import { CDN_URL } from "../../../../../constants";

const VIDEO_EXTENSIONS = [".mp4", ".webm", ".ogv", ".mov", ".avi", ".mkv"];

export const isVideoAsset = (path?: string | null): boolean => {
  if (!path) return false;
  const lowerPath = path.toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => lowerPath.endsWith(ext));
};

// Convert a relative asset path to full CDN URL
export const getAssetUrl = (assetPath?: string | null): string | undefined => {
  if (!assetPath) return undefined;
  // If it's already a full URL, return as-is
  if (assetPath.startsWith("http://") || assetPath.startsWith("https://")) {
    return assetPath;
  }
  // Prepend CDN_URL
  const baseUrl = CDN_URL.endsWith("/") ? CDN_URL.slice(0, -1) : CDN_URL;
  const path = assetPath.startsWith("/") ? assetPath : `/${assetPath}`;
  return `${baseUrl}${path}`;
};

// Match a slide segment: [[SLIDE-id]]content up to the next opening tag
// (or end). Closing tags `[[/SLIDE-id]]` are tolerated inside the content.
const SLIDE_SEGMENT_REGEX =
  /\[\[SLIDE-([^/[\]][^[\]]*)\]\]([\s\S]*?)(?=\[\[SLIDE-[^/[\]][^[\]]*\]\]|$)/g;

const escapeRegex = (value: string) =>
  value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

const slideOpenTagRegex = (uuid: string) =>
  new RegExp(String.raw`\[\[SLIDE-${escapeRegex(uuid)}\]\]`);

const NEXT_SLIDE_OPEN_REGEX = /\[\[SLIDE-[^/[\]][^[\]]*\]\]/g;

// Parse script content by [[SLIDE-{ID}]] tags and return a map with slide ID as key
export const parseScriptBySlides = (
  scriptContent: string
): Map<string, string> => {
  if (!scriptContent) return new Map();

  const result = new Map<string, string>();
  // Match slide ID and content between [[SLIDE-{ID}]] tags
  const regex = new RegExp(SLIDE_SEGMENT_REGEX.source, "g");

  // Check if there's text before the first tag
  const firstTagIndex = scriptContent.indexOf("[[SLIDE-");
  const textBeforeFirstTag =
    firstTagIndex > 0 ? scriptContent.substring(0, firstTagIndex).trim() : "";

  let match: RegExpExecArray | null;
  let isFirstMatch = true;

  while ((match = regex.exec(scriptContent)) !== null) {
    const slideId = match[1];
    let scriptText = match[2];

    if (slideId && scriptText !== undefined) {
      // Prepend text before first tag to the first slide's content
      if (isFirstMatch && textBeforeFirstTag) {
        scriptText = textBeforeFirstTag + " " + scriptText;
        isFirstMatch = false;
      }
      result.set(slideId, scriptText.trim());
    }
  }

  return result;
};

/**
 * For highlight tags that are spanning multiple slides,
 * if user removed one of the HL parts from some slides,
 * we need to add the missing parts to the script correctly.
 * - Closing tag without opening → add opening tag before the content
 * - Opening tag without closing → add closing tag after the content
 */
const reconstructOrphanHighlightTags = (script: string): string => {
  const openingRegex = /\[\[HL-([a-z0-9-]+)\]\]/g;
  const closingRegex = /\[\[\/HL-([a-z0-9-]+)\]\]/g;

  // Collect all opening and closing tag IDs
  const openingIds = new Set<string>();
  const closingIds = new Set<string>();

  let match: RegExpExecArray | null;
  while ((match = openingRegex.exec(script)) !== null) {
    openingIds.add(match[1]);
  }
  while ((match = closingRegex.exec(script)) !== null) {
    closingIds.add(match[1]);
  }

  let result = script;

  // For each closing tag without opening, add opening tag before content
  for (const id of closingIds) {
    if (!openingIds.has(id)) {
      const closingTag = `[[/HL-${id}]]`;
      const closingIndex = result.indexOf(closingTag);
      if (closingIndex !== -1) {
        // Find the start of content (after previous tag or start of text)
        const slideTagBefore = result.lastIndexOf("]]", closingIndex - 1);
        const insertPos = slideTagBefore !== -1 ? slideTagBefore + 2 : 0;
        result =
          result.slice(0, insertPos) + `[[HL-${id}]]` + result.slice(insertPos);
      }
    }
  }

  // For each opening tag without closing, add closing tag after content
  for (const id of openingIds) {
    if (!closingIds.has(id)) {
      const openingTag = `[[HL-${id}]]`;
      const openingIndex = result.indexOf(openingTag);
      if (openingIndex !== -1) {
        // Find the end of content (before next tag or end of text)
        const nextTagIndex = result.indexOf(
          "[[",
          openingIndex + openingTag.length
        );
        const insertPos = nextTagIndex !== -1 ? nextTagIndex : result.length;
        result =
          result.slice(0, insertPos) +
          `[[/HL-${id}]]` +
          result.slice(insertPos);
      }
    }
  }

  return result;
};

// Reconstruct full script with [[SLIDE-{ID}]] placeholders from edited slide content
// This function replaces a specific slide's content in the original script
export const reconstructScriptWithSlide = (
  originalScript: string,
  slideId: string,
  editedSlideContent: string
): string => {
  if (!originalScript) return "";

  // Parse all slides to get their IDs and original content
  const regex = new RegExp(SLIDE_SEGMENT_REGEX.source, "g");
  const slides: Array<{ id: string; originalContent: string }> = [];

  let match: RegExpExecArray | null;
  while ((match = regex.exec(originalScript)) !== null) {
    const id = match[1];
    const content = match[2];
    slides.push({
      id,
      originalContent: content,
    });
  }

  // Find the slide being edited
  const slideIndex = slides.findIndex((slide) => slide.id === slideId);
  if (slideIndex === -1) {
    // Slide not found, return original
    return originalScript;
  }

  // Build the reconstructed script
  let reconstructed = "";

  // Add all slides before the edited one
  for (let i = 0; i < slideIndex; i++) {
    const slide = slides[i];
    reconstructed += `[[SLIDE-${slide.id}]]${slide.originalContent}`;
  }

  // Add the edited slide
  reconstructed += `[[SLIDE-${slideId}]]${editedSlideContent.trim()}`;

  // Add all slides after the edited one
  for (let i = slideIndex + 1; i < slides.length; i++) {
    const slide = slides[i];
    reconstructed += `[[SLIDE-${slide.id}]]${slide.originalContent}`;
  }

  return reconstructOrphanHighlightTags(reconstructed);
};

// Matches an opening HL tag at the end of the supplied string.
const TRAILING_HL_OPEN_REGEX = /\[\[HL-[^/[\]][^[\]]*\]\]$/;

/**
 * Insert a slide segment for `newSlideUuid` (with `narration`) just before the
 * opening tag of `afterSlideUuid` — so the new slide ends up immediately
 * before that slide in the timeline. Pass `afterSlideUuid: null` to append at
 * the end of the script. Slides use open-only tags (`[[SLIDE-uuid]]content`),
 * with the next slide's opening tag acting as the implicit terminator.
 *
 * If one or more opening HL tags sit directly before the next slide's tag
 * (`…[[HL-x]][[SLIDE-uuid]]`), the new slide is placed before them so the
 * highlights stay anchored to the original slide.
 */
export const insertSlide = (
  script: string,
  newSlideUuid: string,
  afterSlideUuid: string | null,
  narration: string
): string => {
  const segment = `[[SLIDE-${newSlideUuid}]]${narration}`;
  if (!script) return segment;
  if (afterSlideUuid === null) return script + segment;

  const open = slideOpenTagRegex(afterSlideUuid).exec(script);
  if (!open) return script + segment;

  let insertAt = open.index;
  while (insertAt > 0) {
    const trailingHl = TRAILING_HL_OPEN_REGEX.exec(script.slice(0, insertAt));
    if (!trailingHl) break;
    insertAt = trailingHl.index;
  }

  return script.slice(0, insertAt) + segment + script.slice(insertAt);
};

/**
 * Remove the slide segment for `slideUuid`: the opening tag and the narration
 * up to (but not including) the next slide's opening tag, or end of script.
 * HL/EMO/PRN tags that lived inside that span are dropped with it.
 */
export const removeSlideSegment = (
  script: string,
  slideUuid: string
): string => {
  if (!script) return script;

  const open = slideOpenTagRegex(slideUuid).exec(script);
  if (!open) return script;

  const afterOpenEnd = open.index + open[0].length;
  const nextOpenRegex = new RegExp(NEXT_SLIDE_OPEN_REGEX.source, "g");
  nextOpenRegex.lastIndex = afterOpenEnd;
  const nextOpen = nextOpenRegex.exec(script);
  const end = nextOpen ? nextOpen.index : script.length;

  return script.slice(0, open.index) + script.slice(end);
};

// Splits a script into its raw `[[SLIDE-uuid]]…` segments (keyed by uuid, in
// document order) plus any text before the first slide tag. Slide uuids are
// unique within a script, so a repeat would be malformed — the first wins.
const parseSlideSegments = (
  script: string
): { segments: Map<string, string>; order: string[]; preamble: string } => {
  const regex = new RegExp(SLIDE_SEGMENT_REGEX.source, "g");
  const segments = new Map<string, string>();
  const order: string[] = [];
  let firstTagIndex = -1;

  let match: RegExpExecArray | null;
  while ((match = regex.exec(script)) !== null) {
    if (firstTagIndex === -1) firstTagIndex = match.index;
    const uuid = match[1];
    if (uuid && !segments.has(uuid)) {
      segments.set(uuid, match[0]);
      order.push(uuid);
    }
  }

  const preamble = firstTagIndex > 0 ? script.slice(0, firstTagIndex) : "";
  return { segments, order, preamble };
};

/**
 * Reorder a section's slide segments to match `orderedUuids`. Each segment
 * (`[[SLIDE-uuid]]…` up to the next slide tag) is moved as a raw block, so the
 * narration and any inner HL/EMO/PRN markers are preserved exactly.
 *
 * Any text before the first slide tag is kept as a preamble, and segments whose
 * uuid is absent from `orderedUuids` are appended in their original order so no
 * narration is ever lost.
 */
export const reorderScriptSlides = (
  script: string,
  orderedUuids: string[]
): string => {
  if (!script) return script;

  const { segments, order, preamble } = parseSlideSegments(script);
  if (segments.size === 0) return script;

  const seen = new Set<string>();
  const ordered: string[] = [];

  for (const uuid of orderedUuids) {
    const segment = segments.get(uuid);
    if (segment && !seen.has(uuid)) {
      ordered.push(segment);
      seen.add(uuid);
    }
  }
  // Preserve any segments not referenced in orderedUuids (defensive).
  for (const uuid of order) {
    if (!seen.has(uuid)) {
      const segment = segments.get(uuid);
      if (segment) ordered.push(segment);
    }
  }

  return preamble + ordered.join("");
};

/**
 * Removes `slideUuid`'s segment but, instead of discarding its narration,
 * appends it to the PREVIOUS slide's segment (so a deleted scene's script is
 * kept on the scene before it). Falls back to a plain removal when the slide is
 * the first segment or absent (no previous to merge into). Inner HL/EMO/PRN
 * markers ride along with the narration; orphan HL tags are reconstructed by
 * `reconstructScriptWithSlide`.
 */
export const mergeSlideScriptIntoPrevious = (
  script: string,
  slideUuid: string
): string => {
  if (!script) return script;

  const { order } = parseSlideSegments(script);
  const index = order.indexOf(slideUuid);
  if (index <= 0) return removeSlideSegment(script, slideUuid);

  const prevUuid = order[index - 1];
  if (!prevUuid) return removeSlideSegment(script, slideUuid);

  const contentByUuid = parseScriptBySlides(script);
  const merged = [contentByUuid.get(prevUuid), contentByUuid.get(slideUuid)]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

  return removeSlideSegment(
    reconstructScriptWithSlide(script, prevUuid, merged),
    slideUuid
  );
};
